import { isEmail } from 'utils/ui'

// Type definitions
interface DefConfig {
  tokenSeparators: string[]
  editAreaClsName: string
  itemClsName: string
  emptyClsName: string
  emailClsName: string
  notEmailClsName: string
  innerEmailClsName: string
  outerEmailClsName: string
  zwsp: string
  cursorStr: string
  blockEditable: boolean
  pasteRegex: RegExp
}

interface EmailInfo {
  email?: string
  name?: string
}

interface MailItem {
  id?: string
  clsNames: string[]
  needFocus?: boolean
  cursorPos?: number | null
  text?: string
}

interface FlattenResult {
  cursorElem: MailItem | null
  cursorPos: number
  items: MailItem[]
}

interface SavedRange {
  sContainer: Node
  sOffset: number
  eContainer: Node
  eOffset: number
}

export const def: DefConfig = {
  // space - keyCode - 32, 160
  tokenSeparators: [',', ';', ' ', ' ', '\xa0'],
  editAreaClsName: 'edit-area',
  itemClsName: 'edit-item',
  emptyClsName: 'empty-item',
  emailClsName: 'mail-item',
  notEmailClsName: 'not-email',
  innerEmailClsName: 'inner-email',
  outerEmailClsName: 'outer-email',
  zwsp: '\u200B',
  cursorStr: '\u200B\u200B\u2038\u200B\u200B',
  blockEditable: false,
  pasteRegex:
    /(?:(.*) ?(?:<([\w!#$%&'*+-/=?^_`{|}~\.]+@(?:[\w-]+\.)+[\w-]{2,63})>))|(?:(.*) +([\w-\.]+@(?:[\w-]+\.)+[\w-]{2,63}))|(?:(.*) ?)/g,
}

export const parseRFC822Email = (text: string): EmailInfo => {
  const objText = text.replace(/\u200B/g, ' ').trim()

  const array = [...objText.matchAll(def.pasteRegex)]
  const info: EmailInfo = {}
  if (array.length > 0) {
    const group = array[0]
    if (group[5] != null && group[5].length > 0) {
      if (isEmail(group[5]) === true) {
        info.email = group[5]
      } else {
        info.name = group[5]
      }
    } else {
      let nameStr = group[1] || group[3]
      let emailStr = group[2] || group[4]

      if (nameStr != null) {
        info.name = nameStr.trim()
        if (info.name.startsWith('"') === true && info.name.endsWith('"') === true) {
          info.name = info.name.slice(1, -1)
        }
      }
      if (emailStr != null) {
        info.email = emailStr.trim()
      }
    }
  }

  return info
}

const checkEmail = (text: string | null | undefined): boolean => {
  if (text == null || text.trim().length === 0) {
    return false
  }

  let tmp = text
  if (tmp.startsWith('<') === true) {
    tmp = tmp.substring(1)
  }
  if (tmp.endsWith('>') === true) {
    tmp = tmp.substring(0, tmp.length - 1)
  }
  return isEmail(tmp)
}

const helper = {
  getFirstTextNode: (node: Node): Node | null => {
    if (node.nodeType === Node.TEXT_NODE) {
      let tmp = node
      while (tmp.previousSibling != null && tmp.previousSibling.nodeType !== Node.ELEMENT_NODE) {
        tmp = tmp.previousSibling
      }
      return tmp
    }
    return null
  },

  isEnableSelect: (node: Node): boolean => {
    if (node.nodeType === Node.TEXT_NODE || node.nodeType === Node.ELEMENT_NODE) {
      return true
    }
    return false
  },

  getPrevTextNode: (node: Node, checkFromSelf?: boolean): Node | null => {
    let tmp: Node | null = checkFromSelf === true ? node : node.previousSibling
    while (tmp != null && helper.isEnableSelect(tmp) !== true) {
      tmp = tmp.previousSibling
    }

    if (
      tmp != null &&
      tmp.nodeType === Node.ELEMENT_NODE &&
      (tmp as HTMLElement).classList.contains(def.itemClsName) === true &&
      tmp.lastChild != null
    ) {
      return helper.getPrevTextNode(tmp.lastChild, true)
    }

    return tmp
  },

  getFlatText: (elems: Node[] /* , forPaste */): string => {
    const textArr: string[] = []
    let arrIdx = 0
    const items = [...elems]
    items.forEach((elem) => {
      while (textArr.length < arrIdx + 1) {
        textArr.push('')
      }
      if (
        elem.nodeType === Node.ELEMENT_NODE &&
        (elem as HTMLElement).classList.contains(def.itemClsName) === true
      ) {
        const element = elem as HTMLElement
        const email = element.getAttribute('data-email')
        const name = element.getAttribute('data-name')

        let text = ''
        if (name != null && email != null) {
          if (name.trim().indexOf(' ') > -1) {
            text = `"${name}" <${email}>`
          } else {
            text = `"${name}" <${email}>`
          }
          textArr[arrIdx] = text
          arrIdx += 1
        } else if (email != null) {
          text = `<${email}>`
          textArr[arrIdx] = text
          arrIdx += 1
        } else if (name != null) {
          if (name.trim().indexOf(' ') > -1) {
            text = `"${name}"`
          } else {
            text = name
          }
          textArr[arrIdx] += text
          arrIdx += 1
        } else {
          text = elem.textContent || ''
          // if (forPaste !== true) {
          const splitArr = text.split(def.cursorStr)
          const prevText = splitArr[0].replace(/\u200B/g, '')
          text = prevText
          if (splitArr.length > 1) {
            const nextText = splitArr[1].replace(/\u200B/g, '')
            text = `${prevText}${def.cursorStr}${nextText}`
          }
          // }
          textArr[arrIdx] += text
          arrIdx += 1
        }
      } else if (elem.nodeType === Node.TEXT_NODE || elem.nodeType === Node.ELEMENT_NODE) {
        const text = elem.textContent || ''
        textArr[arrIdx] += text
        arrIdx += 1
      }
    })
    const allText = textArr.filter((item) => item != null && item.trim().length > 0).join('; ')
    return allText
  },

  flatten: (
    node: Node,
    createNewMailItem: (text: string, className: string, cursorPos?: number | null) => MailItem,
    oldItems: MailItem[],
    cursorToLast?: boolean,
  ): FlattenResult | null => {
    // node가 변경되면 obj, 아니면 null 반환.
    const container = (node as HTMLElement).closest(`.${def.editAreaClsName}`)
    if (!container) return null

    let isSelected = false
    const selection = window.getSelection()
    let id: string | null = null
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0)
      if (range.collapsed === true && container.contains(range.startContainer) === true) {
        isSelected = true

        // const cursorNode = document.createTextNode(def.cursorStr);
        const cursorPosElem = document.createElement('span')
        cursorPosElem.className = 'cursor-pos'
        id = `cursorPos-${new Date().getTime()}`
        cursorPosElem.id = id
        range.insertNode(cursorPosElem)
      }
    }

    const clonedContainer = container.cloneNode(true) as HTMLElement

    if (isSelected === true && id != null) {
      const cursorElem = clonedContainer.querySelector(`#${id}`)
      if (cursorElem) {
        if (cursorElem.closest(`.${def.emailClsName}`) != null) {
          cursorElem.innerHTML = def.cursorStr
        } else {
          cursorElem.innerHTML = ` ${def.cursorStr}`
        }
      }
    }

    const cloneNodes = Array.from(clonedContainer.childNodes)
    const text = helper.getFlatText(cloneNodes)

    const textArr = text.split(def.cursorStr)

    if (textArr.length > 0) {
      const prevText = textArr[0].replace(/\u200B|;/g, ' ')
      const nextText = textArr.length > 1 ? textArr[1].replace(/\u200B|;/g, ' ') : ''

      const flatText = `${prevText}${def.cursorStr}${nextText}`

      console.log(' -- flatText -- ', flatText)

      // const itemArr = flatText
      //   .split(/ (?![<])/g)
      //   .filter((item) => item != null && item.trim().length > 0)

      const regexp = / *(\".*?\")* *(.*?)(?:;| |$)+/gm
      const matches = [...flatText.matchAll(regexp)]
      const splitArr: string[] = []
      matches.forEach((match) => {
        if (match[1] != null && match[1].trim().length > 0) {
          splitArr.push(match[1].trim())
        }
        if (match[2] != null && match[2].trim().length > 0) {
          splitArr.push(match[2].trim())
        }
      })
      const itemArr: string[] = []
      let prevEmail = true
      splitArr.forEach((item, idx) => {
        const isEmailVal = checkEmail(item)
        if (isEmailVal === true && idx > 0 && prevEmail === false) {
          itemArr[itemArr.length - 1] += ' ' + item
          prevEmail = true
        } else {
          itemArr.push(item)
          prevEmail = isEmailVal
        }
      })

      let elemWithCursor: MailItem | null = null
      let cursorPos = -1

      let items: MailItem[] = []
      let elem = createNewMailItem(def.zwsp, def.emptyClsName)
      items.push(elem)
      let isEmptyLast = true
      let focusPos = -1
      itemArr.forEach((item, idx) => {
        const pos = item.indexOf(def.cursorStr)
        if (pos > -1) {
          cursorPos = pos
          focusPos = idx + 1
          const itemText = item.replace(def.cursorStr, '')
          if (itemText.trim().length === 0 && items.length > 0) {
            const prevItem = items[items.length - 1]
            if (prevItem.clsNames.indexOf(def.emptyClsName) === -1) {
              elem = createNewMailItem(def.zwsp, def.emptyClsName, 1)
              items.push(elem)
            } else {
              prevItem.needFocus = true
            }
            isEmptyLast = true
          } else {
            if (pos === 0 && cursorToLast !== true) {
              items[items.length - 1] = createNewMailItem(def.zwsp, def.emptyClsName, 1)
            }
            elem = createNewMailItem(`${def.zwsp}${itemText}`, def.emailClsName)
            items.push(elem)
            isEmptyLast = false
          }
          elemWithCursor = elem
        } else {
          const curPos = focusPos === idx ? 1 : null
          elem = createNewMailItem(`${def.zwsp}${item}`, def.emailClsName, curPos)
          items.push(elem)
          isEmptyLast = false
        }
        if (isEmptyLast === false) {
          if (cursorToLast === true || (idx === itemArr.length - 1 && elemWithCursor == null)) {
            elem = createNewMailItem(def.zwsp, def.emptyClsName, 1)
            isEmptyLast = true
          } else {
            elem = createNewMailItem(def.zwsp, def.emptyClsName)
            isEmptyLast = true
          }
          items.push(elem)
        }
      })

      let child = container.firstChild
      while (child != null) {
        const next = child.nextSibling
        if (
          child.nodeType !== Node.ELEMENT_NODE ||
          oldItems.find((item) => item.id === (child as HTMLElement).getAttribute('objid')) == null
        ) {
          container.removeChild(child)
        }
        child = next
      }

      return {
        cursorElem: elemWithCursor,
        cursorPos,
        items,
      }
    }

    return null
  },

  saveRange: (): SavedRange | null => {
    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0)
      const prevRng: SavedRange = {
        sContainer: range.startContainer,
        sOffset: range.startOffset,
        eContainer: range.endContainer,
        eOffset: range.endOffset,
      }
      return prevRng
    }
    return null
  },

  restoreRange: function (prevRng: SavedRange): void {
    const range = document.createRange()
    range.setStart(prevRng.sContainer, prevRng.sOffset)
    range.setEnd(prevRng.eContainer, prevRng.eOffset)
    const selection = window.getSelection()
    if (selection) {
      selection.removeAllRanges()
      selection.addRange(range)
    }
  },
}

export default helper
